import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';

/**
 * Пикер контрагентов 1С. Поддерживает поиск по кэшу и создание нового
 * (через POST /api/onec/contractors → INSERT pending_create + триггер push в 1С).
 *
 * value: { id, code, name, syncState } | null
 * onChange(value | null)
 * defaultName — предзаполнение строки поиска при первом открытии (например, ФИО ученика)
 */
export default function OnecContractorPicker({ value, onChange, defaultName, disabled }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    setSearch(defaultName || '');
  }, [open, defaultName]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true); setError(null);
      try {
        const data = await api.get(`/api/onec/contractors?search=${encodeURIComponent(search)}&limit=20`);
        setResults(data.rows || []);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Не удалось загрузить');
      } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [search, open]);

  const onCreate = async () => {
    const name = search.trim() || (defaultName || '').trim();
    if (!name) return;
    setCreating(true); setError(null);
    try {
      const data = await api.post('/api/onec/contractors', { name });
      onChange(data.contractor);
      setOpen(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось создать');
    } finally { setCreating(false); }
  };

  return (
    <div className="onec-picker" ref={ref}>
      <div className="onec-picker__row">
        <button
          type="button"
          className="onec-picker__current"
          onClick={() => !disabled && setOpen((o) => !o)}
          disabled={disabled}
        >
          {value ? (
            <span className="onec-picker__value">
              {value.code
                ? <span className="mono onec-picker__code">{value.code}</span>
                : <span className="onec-picker__pending">ожидает 1С</span>}
              <span className="onec-picker__name">{value.name}</span>
              {value.syncState === 'pending_create' && (
                <span className="onec-picker__badge onec-picker__badge--pending">отправка</span>
              )}
              {value.syncState === 'error' && (
                <span className="onec-picker__badge onec-picker__badge--error">ошибка</span>
              )}
            </span>
          ) : <span className="onec-picker__empty">не привязан</span>}
          <Icon name="chevronDown" size={10} />
        </button>
        {value && !disabled && (
          <button
            type="button"
            className="onec-picker__clear"
            onClick={() => onChange(null)}
            title="Отвязать"
          >
            <Icon name="x" size={12} />
          </button>
        )}
      </div>

      {open && (
        <div className="onec-picker__menu">
          <input
            className="onec-picker__search"
            placeholder="Поиск по имени или коду 1С…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          {error && <div className="onec-picker__error">{error}</div>}
          {(search.trim() || defaultName) && (
            <button
              type="button"
              className="onec-picker__create"
              onClick={onCreate}
              disabled={creating}
            >
              <Icon name="plus" size={11} />
              {creating
                ? 'Создаём…'
                : <>Создать в 1С: «<b>{search.trim() || defaultName}</b>»</>}
            </button>
          )}
          <div className="onec-picker__list">
            {loading && <div className="onec-picker__hint">Поиск…</div>}
            {!loading && results.map((r) => (
              <button
                key={r.id}
                type="button"
                className="onec-picker__item"
                onClick={() => { onChange(r); setOpen(false); }}
              >
                <span className="mono onec-picker__code">{r.code || '—'}</span>
                <span className="onec-picker__name">{r.name}</span>
                {r.syncState === 'pending_create' && <span className="onec-picker__badge onec-picker__badge--pending">ожидает</span>}
                {r.syncState === 'error' && <span className="onec-picker__badge onec-picker__badge--error">ошибка</span>}
              </button>
            ))}
            {!loading && results.length === 0 && (
              <div className="onec-picker__hint">
                {search ? 'Ничего не найдено' : 'Начните вводить, чтобы найти контрагента'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
